import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  monitoringHealth,
  normalizeZabbixEntry,
  pct,
  simplifyOs,
  staleMinutes,
  worstDiskFreePct,
} from "../../utils/zabbix";

function pillClass(kind) {
  if (kind === "ok") return "badge badge-ok";
  if (kind === "bad") return "badge badge-bad";
  if (kind === "warn") return "badge badge-warn";
  return "badge badge-muted";
}

export default function PcMonitorList({
  ordenadores = [],
  zabbixByPc = {},
  pcActivo,
  setPcActivo,
  storageKey,
}) {
  const initial = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(storageKey) || "{}");
    } catch {
      return {};
    }
  })();
  const [q, setQ] = useState(initial.q || "");
  const [fAgentKo, setFAgentKo] = useState(Boolean(initial.fAgentKo));
  const [fProblems, setFProblems] = useState(Boolean(initial.fProblems));
  const [fStale60, setFStale60] = useState(Boolean(initial.fStale60));
  const [fDiskCrit, setFDiskCrit] = useState(Boolean(initial.fDiskCrit));
  const listRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem(
      storageKey,
      JSON.stringify({ q, fAgentKo, fProblems, fStale60, fDiskCrit }),
    );
  }, [storageKey, q, fAgentKo, fProblems, fStale60, fDiskCrit]);

  useEffect(() => {
    const top = Number(sessionStorage.getItem(`${storageKey}:scroll`) || 0);
    const frame = window.requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = top;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [storageKey]);

  const rows = useMemo(() => {
    const query = String(q || "")
      .trim()
      .toLowerCase();
    let out = (ordenadores || []).map((pc) => {
      const entry = zabbixByPc?.[String(pc.id)];
      const z = normalizeZabbixEntry(entry);
      const m = z.metricas;
      const agent = m?.agent_ping;
      return {
        pc,
        z,
        health: monitoringHealth(entry),
        agentOk:
          agent === null || agent === undefined ? null : Number(agent) === 1,
        problemsCount: z.problems.length,
        staleMin: staleMinutes(m?.lastclock),
        worstFree: worstDiskFreePct(z.disks),
      };
    });

    if (query) {
      out = out.filter(({ pc }) =>
        `${pc.descripcion || ""} ${pc.hostname || ""} ${pc.ip || ""} ${pc.so || ""} ${pc.serial || ""}`
          .toLowerCase()
          .includes(query),
      );
    }
    if (fAgentKo) out = out.filter((row) => row.agentOk === false);
    if (fProblems) out = out.filter((row) => row.problemsCount > 0);
    if (fStale60)
      out = out.filter((row) => row.staleMin === null || row.staleMin >= 60);
    if (fDiskCrit)
      out = out.filter(
        (row) => Number.isFinite(row.worstFree) && row.worstFree < 20,
      );

    out.sort((a, b) => {
      if (a.health.priority !== b.health.priority)
        return a.health.priority - b.health.priority;
      if (b.problemsCount !== a.problemsCount)
        return b.problemsCount - a.problemsCount;
      const aDisk = Number.isFinite(a.worstFree) ? a.worstFree : 9999;
      const bDisk = Number.isFinite(b.worstFree) ? b.worstFree : 9999;
      if (aDisk !== bDisk) return aDisk - bDisk;
      return String(a.pc.descripcion || a.pc.hostname || "").localeCompare(
        String(b.pc.descripcion || b.pc.hostname || ""),
      );
    });
    return out;
  }, [ordenadores, zabbixByPc, q, fAgentKo, fProblems, fStale60, fDiskCrit]);

  return (
    <aside className="mon-left card">
      <div className="mon-left-head">
        <div>
          <div className="mon-title">Equipos</div>
          <div className="mon-sub">
            {rows.length === ordenadores.length
              ? `${rows.length} equipos`
              : `${rows.length} de ${ordenadores.length} equipos`}
          </div>
        </div>
      </div>

      <div className="mon-filters">
        <input
          className="input mon-search"
          placeholder="Buscar equipo, host, IP o sistema…"
          value={q}
          onChange={(event) => setQ(event.target.value)}
        />
        <div className="mon-pills">
          <button
            type="button"
            className={`pillBtn ${fAgentKo ? "on" : ""}`}
            aria-pressed={fAgentKo}
            onClick={() => setFAgentKo((value) => !value)}
          >
            Agente caído
          </button>
          <button
            type="button"
            className={`pillBtn ${fProblems ? "on" : ""}`}
            aria-pressed={fProblems}
            onClick={() => setFProblems((value) => !value)}
          >
            Con problemas
          </button>
          <button
            type="button"
            className={`pillBtn ${fDiskCrit ? "on" : ""}`}
            aria-pressed={fDiskCrit}
            onClick={() => setFDiskCrit((value) => !value)}
          >
            Disco crítico
          </button>
          <button
            type="button"
            className={`pillBtn ${fStale60 ? "on" : ""}`}
            aria-pressed={fStale60}
            onClick={() => setFStale60((value) => !value)}
          >
            Sin datos / &gt;60m
          </button>
        </div>
      </div>

      <div
        className="mon-list"
        ref={listRef}
        onScroll={(event) =>
          sessionStorage.setItem(
            `${storageKey}:scroll`,
            String(event.currentTarget.scrollTop),
          )
        }
      >
        {rows.length === 0 ? (
          <div className="mon-empty">No hay equipos con esos filtros.</div>
        ) : (
          rows.map((row) => {
            const { pc, z } = row;
            const active = String(pcActivo) === String(pc.id);
            const cpu = Number(z.metricas?.cpu_pct);
            const ram = Number(z.metricas?.ram_pct);
            const diskBadge =
              Number.isFinite(row.worstFree) && row.worstFree < 30
                ? {
                    kind: row.worstFree < 20 ? "bad" : "warn",
                    text: `Disco ${Math.round(row.worstFree)}% libre`,
                  }
                : null;
            const staleBadge =
              row.staleMin === null
                ? { kind: "muted", text: "Sin telemetría" }
                : row.staleMin >= 60
                  ? { kind: "warn", text: `Sin actualizar ${row.staleMin}m` }
                  : null;

            return (
              <button
                key={pc.id}
                type="button"
                className={`mon-row ${active ? "active" : ""}`}
                onClick={() => setPcActivo?.(pc.id)}
                aria-current={active ? "true" : undefined}
              >
                <div className="mon-row-main">
                  <div className="mon-row-title">
                    {pc.descripcion || pc.hostname || `Equipo ${pc.id}`}
                  </div>
                  <div className="mon-row-sub">
                    {pc.hostname ? `🖥 ${pc.hostname}` : ""}
                    {pc.ip ? ` · ${pc.ip}` : ""}
                    {pc.so ? ` · ${simplifyOs(pc.so)}` : ""}
                  </div>
                  {cpu >= 85 || ram >= 85 ? (
                    <div className="mon-row-mini">
                      {cpu >= 85 ? (
                        <>
                          CPU <strong>{pct(cpu)}</strong>
                        </>
                      ) : null}
                      {ram >= 85 ? (
                        <span className="mon-row-metric">
                          RAM <strong>{pct(ram)}</strong>
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="mon-row-badges">
                  <span className={pillClass(row.health.kind)}>
                    {row.health.label}
                  </span>
                  {row.agentOk === false ? (
                    <span className={pillClass("bad")}>Agente caído</span>
                  ) : null}
                  {row.problemsCount > 0 ? (
                    <span className={pillClass("warn")}>
                      {row.problemsCount} problema(s)
                    </span>
                  ) : null}
                  {diskBadge ? (
                    <span className={pillClass(diskBadge.kind)}>
                      {diskBadge.text}
                    </span>
                  ) : null}
                  {staleBadge ? (
                    <span className={pillClass(staleBadge.kind)}>
                      {staleBadge.text}
                    </span>
                  ) : null}
                </div>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
}
